import { Injectable, NgZone } from '@angular/core';
import { Network } from '@capacitor/network';
import { ToolBarType, InAppBrowser, UrlEvent } from '@capgo/inappbrowser';
import { LoggerService } from './logger/logger.service';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InAppBrowserService {
  public networkConnected = true;

  private browserClosed$ = new Subject<void>();
  private urlChanged$ = new Subject<string>();

  public constructor(
    private zone: NgZone,
    private loggerService: LoggerService,
  ) {
    Network.addListener('networkStatusChange', status => {
      this.zone.run(() => {
        this.networkConnected = status.connected;
      });
    });
  }

  public async open(
    url: string,
    options?: {
      executeScriptAfterLoad: string[], // use with care, UI changes persist multiple windows
    }
  ): Promise<void | null> {
    try {
      await InAppBrowser.openWebView({ url, toolbarType: ToolBarType.BLANK, visibleTitle: false });

      InAppBrowser.addListener('closeEvent', () => {
        this.closeWindow();
      });

      InAppBrowser.addListener('urlChangeEvent', (event: UrlEvent) => {
        this.urlChanged$.next(event.url);
      });

      if(options?.executeScriptAfterLoad && !!InAppBrowser) {
        InAppBrowser.addListener('browserPageLoaded', async () => {
          // reset zoom
          this.executeScript('document.body.style.zoom = 1.0;');

          for (const inputScript of options.executeScriptAfterLoad) {
            await this.executeScript(inputScript);
          }
        });
      }
    } catch (error) {
      this.loggerService.error('Failed to open inAppBrowser!', error);
      return null;
    }
  }

  public close(): void {
    InAppBrowser.removeAllListeners();
    this.closeWindow();
    InAppBrowser.close();
  }

  public onBrowserClose(): Observable<void> {
    return this.browserClosed$.asObservable();
  }

  public onUrlChange(): Observable<string> {
    return this.urlChanged$.asObservable();
  }

  private closeWindow(): void {
    try {
      this.browserClosed$.next();
    } catch (error) {
      this.loggerService.error('Error during browser close window!', error);
    }
  }

  private async executeScript(code: string) {
    if (InAppBrowser) {
      try {
        await InAppBrowser.executeScript({ code });
      } catch (error) {
        this.loggerService.error('Failed to execute afterload script', error);
      }
    }
  }

  public clearSession(): void {
    try {
      InAppBrowser.clearAllCookies();
      InAppBrowser.clearCache();
    } catch (error) {
      this.loggerService.error('Error during browser session reset!', error);
    }
  }
}
